import * as S from './Alert.style';
import { AlertProps } from './Alert.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RiInformation2Line, RiCheckboxCircleLine, RiCloseLine } from 'react-icons/ri';

export default function Alert(alertConfigs: AlertProps) {
  const isList = alertConfigs.isList ?? true;
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const alertBordered = !!alertConfigs.bordered;
  const alertTheme = alertConfigs.theme || 'success';
  const alertBgType = alertConfigs.bgType || 'primary';
  const alertBorderLeft = alertConfigs.borderLeft || false;
  const alertTime = alertConfigs.time || 5000;
  const alertTitle = alertConfigs.title || '';

  useEffect(() => {
    setIsAnimatingOut(false);

    const timeout1 = setTimeout(() => {
      setIsAnimatingOut(true);
    }, alertTime);

    const timeout2 = setTimeout(() => {
      if (alertConfigs.onClose) {
        alertConfigs.onClose();
      }
    }, alertTime + 500);

    timeoutRefs.current.push(timeout1, timeout2);

    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current = [];
    };
  }, [alertTime, alertConfigs]);

  const emphasisColor = S.alertTheme[alertTheme]?.emphasis || S.alertGray;

  const alertMessage = useMemo(() => {
    return typeof alertConfigs.message === 'string' ? (
      <span dangerouslySetInnerHTML={{ __html: alertConfigs.message }} />
    ) : (
      alertConfigs.message
    );
  }, [alertConfigs.message]);

  return (
    <S.Content
      $theme={alertTheme}
      $bgType={alertBgType}
      $bordered={alertBordered}
      $isVisible={!isAnimatingOut}
      $borderLeft={alertBorderLeft}
    >
      <S.Row>
        {alertConfigs.showIcon && alertTheme === 'success' && <RiCheckboxCircleLine size={24} color={emphasisColor} />}
        {alertConfigs.showIcon && alertTheme !== 'success' && <RiInformation2Line size={24} color={emphasisColor} />}

        <S.AlertTitle color={emphasisColor}>
          {typeof alertTitle === 'string' ? <span dangerouslySetInnerHTML={{ __html: alertTitle }} /> : alertTitle}
        </S.AlertTitle>

        {!!alertConfigs.close && (
          <S.CloseButton onClick={alertConfigs.onClose}>
            {alertConfigs.close === 'icon' && <RiCloseLine size={24} color={emphasisColor} />}
            {alertConfigs.close === 'title' && <S.AlertClose color={emphasisColor}>Fechar</S.AlertClose>}
          </S.CloseButton>
        )}
      </S.Row>

      {(!!alertConfigs.confirmTitle || !!alertConfigs.cancelTitle) && (
        <>
          <S.Separator />
          <S.Row $justifyContent="center">
            {!!alertConfigs.confirmTitle && !!alertConfigs.confirmAction && (
              <S.AlertButton
                $theme={alertTheme}
                onClick={() => {
                  alertConfigs.confirmAction?.();
                  alertConfigs.onClose?.();
                }}
              >
                <S.AlertButtonText>{alertConfigs.confirmTitle}</S.AlertButtonText>
              </S.AlertButton>
            )}

            {!!alertConfigs.cancelTitle && !!alertConfigs.cancelAction && (
              <S.AlertButton
                $theme={alertTheme}
                onClick={() => {
                  alertConfigs.cancelAction?.();
                  alertConfigs.onClose?.();
                }}
              >
                <S.AlertButtonText>{alertConfigs.cancelTitle}</S.AlertButtonText>
              </S.AlertButton>
            )}
          </S.Row>
        </>
      )}

      {alertConfigs.message && <S.Separator />}

      {alertConfigs.message && isList && (
        <S.AlertMessageList>
          <S.AlertMessage>{alertMessage}</S.AlertMessage>
        </S.AlertMessageList>
      )}

      {alertConfigs.message && !isList && (
        <S.AlertMessageNormal>
          <S.AlertMessageItem>{alertMessage}</S.AlertMessageItem>
        </S.AlertMessageNormal>
      )}
    </S.Content>
  );
}
